/**
 * The meals and drinks (spec 02 §7.3): fries, tuna and shrimp sushi, burger, chicken, ramen,
 * beer, martini; and Chack'n, the hero of Taito's Chack'n Pop, a round yellow chick with big eyes
 * (the C64 item is yellow, colour $0F), worth 10,000.
 */

import { Matrix4, Vector3 } from 'three';
import { type MeshBuilder, place } from '../../kit/MeshBuilder';
import type { Palette } from '../../palette';
import { arcPts, type Builder, chain, extrude, faceCamera, lathe, rotZ, scaled, torus, tube } from './kit';

type Pt = readonly [number, number];

/** A mound of sushi rice, flat underneath. */
function rice(mb: MeshBuilder, p: Palette): void {
  mb.blob(0.4, 0.26, 0.26, p.color('rice.1'), place(0, 0.04, 0), {
    detail: 3, flatBottom: true, topColor: p.color('rice.2'),
  });
}

/**
 * A slice of fish draped over the rice: a flattened tube along x whose ends droop, `bands` giving
 * the colour ramp step of each segment (pale lines across the grain, or a shrimp's stripes).
 */
function topping(mb: MeshBuilder, p: Palette, ramp: string, xs: readonly number[], bands: readonly number[],
  thick: number): void {
  const pts = xs.map((x) => new Vector3(x, -x * x * 0.5, 0));
  const radii = xs.map((x, i) => (i === 0 || i === xs.length - 1 ? 0.6 : 1) * thick * (1 - Math.abs(x) * 0.3));
  tube(mb, pts, radii, (seg) => p.color(`${ramp}.${bands[seg] ?? 1}`), 14, scaled(0, 0.36, 0, 1, 0.5, 2.3));
}

/** The nori belt round a piece of sushi: a thin band following its rounded section. */
function noriBelt(mb: MeshBuilder, p: Palette, x: number): void {
  const outline = [[0.29, 0.03], [0.3, 0.3], ...arcPts(0.14, 0.3, 0.16, 0.18, 0, Math.PI / 2, 4),
    ...arcPts(-0.14, 0.3, 0.16, 0.18, Math.PI / 2, Math.PI, 4), [-0.3, 0.3], [-0.29, 0.03]] as const;
  extrude(mb, outline, 0.13, p.color('nori.1'), chain(new Matrix4().makeTranslation(x, 0, 0),
    new Matrix4().makeRotationY(Math.PI / 2)), p.color('nori.2'));
}

/** A rounded dome profile from radius `r` at `y0` up to the top at `y1`. */
const dome = (r: number, y0: number, y1: number): Pt[] =>
  arcPts(0, y0, r, y1 - y0, 0, Math.PI / 2, 8).map(([x, y]) => [Math.max(0, x), y] as const);

export const MEALS: Record<string, Builder> = {
  fries: (mb, p) => {
    // Golden fries standing in a red carton with a white band.
    const fries: Array<[number, number, number, number]> = [[-0.24, 0.84, 0.18, -0.08], [-0.12, 0.94, 0.06, 0.06],
      [0, 0.88, -0.04, 0.02], [0.12, 0.96, -0.1, 0.1], [0.24, 0.82, -0.2, -0.04], [-0.18, 0.8, 0.02, -0.1],
      [0.06, 0.8, 0.12, 0.12], [0.18, 0.86, 0.1, -0.1], [-0.06, 0.78, -0.14, 0.08]];
    fries.forEach(([x, top, roll, z], i) => {
      mb.box(0.08, top - 0.2, 0.08, p.color(i % 2 ? 'fries.1' : 'fries.2'), rotZ(x * 0.6, 0.2, roll, z));
    });
    const carton = chain(new Matrix4().makeScale(1, 1, 0.55), place(0, 0, 0, Math.PI / 4));
    mb.cylinder(0.42, 0.58, 0.56, 4, p.color('carton.1'), carton, p.color('fries.0'));
    mb.box(0.7, 0.08, 0.02, p.color('paper.0'), place(0, 0.38, 0.23));
    mb.blob(0.1, 0.1, 0.02, p.color('fries.1'), place(0, 0.22, 0.2), { detail: 2 });
  },
  'tuna sushi': (mb, p) => {
    // A thick slice of red tuna draped over a pillow of rice, pale lines across its grain.
    rice(mb, p);
    const xs = [-0.5, -0.46, -0.3, -0.27, -0.1, -0.07, 0.1, 0.13, 0.3, 0.33, 0.46, 0.5];
    topping(mb, p, 'tuna', xs, [1, 1, 2, 1, 2, 1, 2, 1, 2, 1, 1], 0.15);
  },
  'shrimp sushi': (mb, p) => {
    // A butterflied shrimp, orange and white bands, tail fanned up, held on by a belt of nori.
    rice(mb, p);
    const xs = [-0.46, -0.42, -0.3, -0.18, -0.06, 0.06, 0.18, 0.3, 0.4];
    topping(mb, p, 'shrimp', xs, [1, 1, 2, 1, 2, 1, 2, 1], 0.13);
    extrude(mb, [[0, -0.06], [0.2, 0.02], [0.26, 0.22], [0.13, 0.15], [0.02, 0.08]], 0.12, p.color('shrimp.0'),
      new Matrix4().makeTranslation(0.34, 0.3, 0), p.color('shrimp.1'));
    noriBelt(mb, p, -0.04);
  },
  burger: (mb, p) => {
    // Bun, patty, cheese, lettuce, tomato, bun with sesame seeds.
    const round = { sides: 24 };
    const bun = p.color('bun.1');
    lathe(mb, [[0, 0], [0.38, 0], [0.43, 0.04], [0.44, 0.1], [0.4, 0.15], [0, 0.15]], bun, undefined, round);
    lathe(mb, [[0, 0.14], [0.44, 0.14], [0.47, 0.2], [0.44, 0.27], [0, 0.27]], p.color('choc.1'), undefined, round);
    mb.box(0.72, 0.035, 0.72, p.color('cheese.1'), place(0, 0.27, 0, Math.PI / 4));
    lathe(mb, [[0, 0.3], [0.5, 0.3], [0.52, 0.33], [0.46, 0.35], [0, 0.35]],
      (_, k) => p.color(k % 2 ? 'leaf.1' : 'leaf.2'), undefined, { sides: 14 });
    lathe(mb, [[0, 0.35], [0.4, 0.35], [0.41, 0.4], [0, 0.4]], p.color('berry.1'), undefined, { sides: 20 });
    lathe(mb, [[0, 0.39], ...dome(0.45, 0.4, 0.82)], bun, undefined, round);
    for (const [a, e] of [[-0.9, 0.5], [-0.3, 0.35], [0.3, 0.5], [0.9, 0.35], [0, 0.85], [-0.6, 0.95], [0.6, 0.9],
      [0.05, 0.3], [-1.3, 0.3], [1.3, 0.55]] as const) {
      const r = 0.45 * Math.cos(e);
      const at = place(Math.sin(a) * r, 0.4 + Math.sin(e) * 0.42, Math.cos(a) * r, a, 1, -e);
      mb.blob(0.035, 0.022, 0.02, p.color('cream.2'), at, { detail: 1 });
    }
  },
  chicken: (mb, p) => {
    // A fried drumstick lying diagonal: crisp golden meat low left, the white bone up right.
    mb.with(rotZ(-0.3, 0.24, -0.95), () => {
      lathe(mb, [[0, -0.18], [0.16, -0.14], [0.27, -0.04], [0.31, 0.1], [0.29, 0.26], [0.2, 0.4], [0.1, 0.5],
        [0.07, 0.56], [0, 0.57]], p.color('fried.1'), undefined, { sides: 18 });
      for (const [y, a] of [[0.0, 0.4], [0.18, -0.5], [0.3, 0.9], [-0.08, -1.2], [0.1, 1.5]] as const) {
        const at = place(Math.sin(a) * 0.28, y, Math.cos(a) * 0.28, a);
        mb.blob(0.07, 0.06, 0.05, p.color('fried.2'), at, { detail: 1 });
      }
      tube(mb, [new Vector3(0, 0.5, 0), new Vector3(0, 0.78, 0)], [0.055, 0.05], p.color('cream.2'), 8);
      mb.blob(0.07, 0.07, 0.07, p.color('cream.2'), place(-0.06, 0.82, 0), { detail: 2 });
      mb.blob(0.07, 0.07, 0.07, p.color('cream.2'), place(0.06, 0.82, 0), { detail: 2 });
    });
  },
  ramen: (mb, p) => mb.with(place(0, 0, 0, 0, 1.08), () => {
    // A red bowl with a white band; golden noodles in brown broth, pork, naruto, green onion; chopsticks.
    lathe(mb, [[0, 0], [0.18, 0], [0.19, 0.05], [0.24, 0.08], [0.36, 0.16], [0.45, 0.3], [0.48, 0.4], [0.49, 0.46],
      [0.45, 0.47], [0, 0.47]], (seg) => p.color(seg === 5 ? 'paper.0' : seg >= 7 ? 'bowl.2' : 'bowl.1'), undefined,
    { sides: 26 });
    mb.blob(0.45, 0.03, 0.45, p.color('broth.1'), place(0, 0.45, 0), { detail: 2 });
    mb.blob(0.3, 0.1, 0.28, p.color('noodle.1'), place(0.02, 0.46, -0.02), { detail: 3 });
    for (const [z, ph] of [[0.14, 0], [0.05, 1.5], [-0.05, 3], [-0.14, 4.5]] as const) {
      const strand: Vector3[] = [];
      for (let i = 0; i <= 8; i++) {
        const x = -0.3 + i * 0.075;
        const w = i * 1.4 + ph;
        strand.push(new Vector3(x, 0.5 + Math.sin(w) * 0.02 - x * x * 0.4, z + Math.cos(w) * 0.02));
      }
      tube(mb, strand, strand.map(() => 0.024), p.color('noodle.2'), 6);
    }
    // Pork on the left, naruto (white with a pink swirl) on the right, tipped up to face out.
    const pork = place(-0.2, 0.5, 0.13, 0.3, 1, 0.9);
    lathe(mb, [[0, 0], [0.13, 0], [0.13, 0.035], [0, 0.035]], (seg) => p.color(seg === 1 ? 'choc.1' : 'choc.2'), pork,
      { sides: 14 });
    const naruto = place(0.19, 0.51, 0.14, -0.3, 1, 0.9);
    lathe(mb, [[0, 0], [0.105, 0], [0.105, 0.035], [0, 0.035]], p.color('paper.0'), naruto, { sides: 14 });
    lathe(mb, [[0.03, 0.034], [0.065, 0.034], [0.065, 0.045], [0.03, 0.045], [0.03, 0.034]], p.color('cheek.1'), naruto,
      { sides: 14 });
    for (const [x, z] of [[-0.02, 0.2], [0.06, 0.24], [0.0, 0.1], [0.1, 0.16], [-0.08, 0.26]] as const) {
      mb.box(0.05, 0.03, 0.05, p.color('leaf.2'), place(x, 0.53, z, x * 20));
    }
    for (const z of [-0.04, 0.04]) {
      mb.box(0.035, 0.8, 0.035, p.color('wood.1'), rotZ(0.12, 0.44, -0.62 + z * 2, z));
    }
  }),
  beer: (mb, p) => {
    // A glass mug of amber beer with a head of foam spilling over, a handle on the right.
    lathe(mb, [[0, 0], [0.3, 0], [0.31, 0.04], [0.31, 0.08], [0.3, 0.08], [0.3, 0.64], [0, 0.64]],
      (seg) => p.color(seg < 3 ? 'glass.1' : 'beer.1'), undefined, { sides: 22 });
    mb.blob(0.08, 0.36, 0.03, p.color('beer.2'), place(-0.14, 0.36, 0.26), { detail: 1 });
    mb.blob(0.35, 0.16, 0.35, p.color('cream.1'), place(0, 0.68, 0), { detail: 3, topColor: p.color('cream.2') });
    for (const [a, len] of [[-0.6, 0.1], [0.1, 0.16], [0.7, 0.08]] as const) {
      const at = place(Math.sin(a) * 0.3, 0.62 - len * 0.3, Math.cos(a) * 0.3, a);
      mb.blob(0.07, len, 0.05, p.color('cream.1'), at, { detail: 1 });
    }
    torus(mb, 0.17, 0.055, p.color('glass.2'), place(0.3, 0.34, 0), 16, 8, Math.PI, -Math.PI / 2);
  },
  martini: (mb, p) => {
    // A cocktail glass on a thin stem, an olive on a red pick.
    lathe(mb, [[0, 0], [0.26, 0], [0.27, 0.03], [0.05, 0.05], [0.035, 0.1], [0.035, 0.42], [0.06, 0.46], [0.44, 0.84],
      [0.4, 0.84], [0, 0.8]], (seg) => p.color(seg === 8 ? 'drink.1' : seg >= 6 ? 'glass.2' : 'glass.1'), undefined,
    { sides: 22 });
    mb.blob(0.08, 0.08, 0.08, p.color('olive.1'), place(0.1, 0.84, 0.05), { detail: 2 });
    mb.blob(0.03, 0.03, 0.02, p.color('berry.1'), place(0.09, 0.86, 0.13), { detail: 1 });
    tube(mb, [new Vector3(0.02, 0.76, 0.03), new Vector3(0.3, 1.04, 0.1)], [0.018, 0.018], p.color('berry.1'), 5);
  },
  "chack'n": (mb, p) => {
    // A round yellow chick: big eyes, a little beak, a tuft on top, pink feet.
    mb.blob(0.44, 0.4, 0.38, p.color('chackn.1'), place(0, 0.46, 0), { detail: 3, topColor: p.color('chackn.2') });
    for (const x of [-0.15, 0.15]) {
      mb.blob(0.12, 0.17, 0.06, p.color('eye.1'), place(x, 0.54, 0.31), { detail: 2 });
      mb.blob(0.065, 0.11, 0.04, p.color('eye.0'), place(x * 0.9, 0.52, 0.36), { detail: 2 });
      mb.blob(0.08, 0.05, 0.03, p.color('cheek.1'), place(x * 2.1, 0.38, 0.28), { detail: 1 });
      mb.blob(0.14, 0.06, 0.12, p.color('limb.1'), place(x * 1.3, 0.05, 0.08), { detail: 2, flatBottom: true });
      const wing = rotZ(Math.sign(x) * 0.42, 0.46, Math.sign(x) * 0.6);
      mb.blob(0.07, 0.16, 0.12, p.color('chackn.1'), wing, { detail: 2 });
    }
    mb.cylinder(0.06, 0, 0.12, 8, p.color('beak.1'), chain(place(0, 0.38, 0.34), faceCamera()));
    const tuft = [new Vector3(0, 0.82, 0), new Vector3(0.02, 0.96, 0), new Vector3(0.12, 1.02, 0),
      new Vector3(0.16, 0.94, 0)];
    tube(mb, tuft,
      [0.05, 0.04, 0.03, 0.02], p.color('chackn.1'), 8);
  },
};
